import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import { CheckCircleRounded, ShieldOutlined } from "@mui/icons-material";
import { Link } from "react-router-dom";

export default function AuthShell({
  eyebrow,
  title,
  subtitle,
  formTitle,
  formSubtitle,
  highlights = [],
  children,
}) {
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        bgcolor: "background.default",
        display: "grid",
        alignItems: "center",
        py: { xs: 0, md: 4 },
      }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: { xs: "100dvh", md: 720 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(420px, .85fr)" },
            overflow: "hidden",
            border: { xs: 0, md: "1px solid" },
            borderColor: { md: "divider" },
            borderRadius: { xs: 0, md: 4 },
            boxShadow: { xs: "none", md: "0 24px 70px rgba(48,72,216,.14)" },
          }}
        >
          <Box
            component="section"
            aria-labelledby="auth-title"
            sx={{
              background: "linear-gradient(145deg, #3048d8 0%, #4f6bff 58%, #2bb7e8 135%)",
              color: "#fff",
              p: { xs: 3, sm: 5, md: 7 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: { xs: 280, md: "auto" },
            }}
          >
            <Stack
              component={Link}
              to="/"
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{ color: "inherit", textDecoration: "none", width: "fit-content", minHeight: 44 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#fff",
                  color: "primary.dark",
                }}
              >
                <ShieldOutlined aria-hidden="true" />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>Evaluate Yourself</Typography>
            </Stack>

            <Box sx={{ my: { xs: 3, md: 6 }, maxWidth: 540 }}>
              <Typography
                variant="overline"
                sx={{ color: "#dbe1ff", fontWeight: 750, letterSpacing: ".08em" }}
              >
                {eyebrow}
              </Typography>
              <Typography
                id="auth-title"
                component="h1"
                sx={{ mt: 1.5, fontSize: { xs: 32, sm: 40, md: 46 }, fontWeight: 800, lineHeight: 1.12 }}
              >
                {title}
              </Typography>
              <Typography sx={{ mt: 2, color: "#e3e8ff", fontSize: { xs: 15, md: 17 }, lineHeight: 1.65 }}>
                {subtitle}
              </Typography>
            </Box>

            {highlights.length > 0 && (
              <Stack component="ul" spacing={1.75} sx={{ p: 0, m: 0, listStyle: "none", display: { xs: "none", sm: "flex" } }}>
                {highlights.map((item) => (
                  <Stack component="li" key={item.title} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircleRounded aria-hidden="true" sx={{ color: "#9eb0ff", fontSize: 22, mt: 0.1 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 750, fontSize: 14 }}>{item.title}</Typography>
                      <Typography variant="body2" sx={{ color: "#dbe1ff", mt: 0.25 }}>{item.body}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          <Box
            component="section"
            aria-label={formTitle || eyebrow}
            sx={{
              bgcolor: "background.paper",
              p: { xs: 3, sm: 5, md: 7 },
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", maxWidth: 440, mx: "auto" }}>
              {formTitle && (
                <Box sx={{ mb: 3 }}>
                  <Typography component="h2" sx={{ fontSize: { xs: 27, md: 30 }, fontWeight: 800, lineHeight: 1.2 }}>
                    {formTitle}
                  </Typography>
                  {formSubtitle && (
                    <Typography sx={{ color: "#6b7280", mt: 1, lineHeight: 1.6 }}>{formSubtitle}</Typography>
                  )}
                </Box>
              )}
              {children}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
